/**
 * The chrome shared by every onboarding step: the thin progress bar, the
 * `Step N of 6` line, the headline, and the optional sub line. A new step
 * renders inside it rather than drawing its own progress bar.
 */
import type { ReactNode } from "react";

import { stepIndicatorLabel } from "./onboardingCopy";
import { stepFillPercent } from "./onboardingSteps";

export const OnboardingStepFrame = ({
  stepIndex,
  stepTotal,
  headline,
  sub,
  isWide = false,
  children,
}: {
  stepIndex: number;
  /**
   * The number of steps THIS fan sees, per Q274. Required, not optional with
   * a default: a default is exactly how a caller silently keeps printing
   * "of 6" to a five-step fan.
   */
  stepTotal: number;
  headline: string;
  /** Omitted on the under-13 refusal, which drops the sub line. */
  sub?: string | undefined;
  /** True on the sports and leagues picker steps, which need a wider card for
   *  their grids at desktop width. Every other step stays at 480px. */
  isWide?: boolean | undefined;
  children: ReactNode;
}): ReactNode => (
  <div
    data-testid="onboarding-step-frame"
    className={
      isWide
        ? "mx-auto flex w-full max-w-[720px] flex-col gap-4 py-6 lg:rounded-md lg:border lg:border-line lg:bg-surface lg:p-6"
        : "mx-auto flex w-full max-w-[480px] flex-col gap-4 py-6 lg:rounded-md lg:border lg:border-line lg:bg-surface lg:p-6"
    }
  >
    <div className="flex items-center gap-2">
      <div
        role="progressbar"
        aria-valuenow={stepIndex}
        aria-valuemin={1}
        aria-valuemax={stepTotal}
        aria-valuetext={stepIndicatorLabel(stepIndex, stepTotal)}
        className="h-1 flex-1 overflow-hidden rounded-full bg-hairline"
      >
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${stepFillPercent(stepIndex, stepTotal)}%` }}
        />
      </div>
      <span
        className="text-caption whitespace-nowrap text-ink-tertiary"
        data-testid="onboarding-step-indicator"
      >
        {stepIndicatorLabel(stepIndex, stepTotal)}
      </span>
    </div>
    <h1 className="text-h1 text-ink">{headline}</h1>
    {sub === undefined || sub === "" ? null : (
      <p className="text-body text-ink-secondary">{sub}</p>
    )}
    {children}
  </div>
);
